/**
 * Compact issues / negotiation points panel for Word add-in (320px).
 *
 * Shows open/resolved issues with deal-breaker badges, positions,
 * and inline status changes. Displayed in the status bottom sheet.
 */

import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Scale,
  ChevronDown,
  ChevronUp,
  Plus,
  X,
  AlertOctagon,
  Pencil,
  Check,
} from 'lucide-react'
import {
  useIssues,
  useCreateIssue,
  useUpdateIssue,
  useDeleteIssue,
} from '../../hooks/useNegotiations'
import type { NegotiationIssue, IssueStatus } from '../../services/negotiationsApi'

type Filter = 'all' | 'open' | 'deal-breakers' | 'resolved'

const STATUS_STYLES: Record<IssueStatus, { bg: string; text: string; label: string }> = {
  OPEN: { bg: 'bg-neutral-100 dark:bg-neutral-700', text: 'text-neutral-600 dark:text-neutral-300', label: 'Open' },
  PROPOSED: { bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', label: 'Proposed' },
  COUNTER_PROPOSED: { bg: 'bg-purple-50 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', label: 'Counter' },
  ACCEPTED: { bg: 'bg-success-50 dark:bg-success-900/30', text: 'text-success-700 dark:text-success-300', label: 'Accepted' },
  REJECTED: { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'Rejected' },
  DEFERRED: { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', label: 'Deferred' },
}

interface IssuesPanelProps {
  negotiationId: number
  issues?: NegotiationIssue[] | undefined
  isLoading?: boolean
  alwaysExpanded?: boolean
}

export function IssuesPanel({
  negotiationId,
  issues: issuesProp,
  isLoading: isLoadingProp,
  alwaysExpanded,
}: IssuesPanelProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(alwaysExpanded ?? false)

  // Self-fetch when used standalone in tab mode
  const selfFetch = useIssues(issuesProp === undefined ? negotiationId : undefined)
  const issues = issuesProp ?? selfFetch.data
  const isLoading = isLoadingProp ?? selfFetch.isLoading
  const [filter, setFilter] = useState<Filter>('all')
  const [showCreate, setShowCreate] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newCategory, setNewCategory] = useState('')
  const [newDealBreaker, setNewDealBreaker] = useState(false)

  const createMut = useCreateIssue()

  const allIssues = useMemo(() => issues ?? [], [issues])

  const openCount = useMemo(
    () => allIssues.filter((i) => i.status !== 'ACCEPTED').length,
    [allIssues],
  )

  const dealBreakerCount = useMemo(
    () => allIssues.filter((i) => i.is_deal_breaker && i.status !== 'ACCEPTED').length,
    [allIssues],
  )

  const filtered = useMemo(() => {
    switch (filter) {
      case 'open':
        return allIssues.filter((i) => i.status !== 'ACCEPTED')
      case 'deal-breakers':
        return allIssues.filter((i) => i.is_deal_breaker && i.status !== 'ACCEPTED')
      case 'resolved':
        return allIssues.filter((i) => i.status === 'ACCEPTED')
      default:
        return allIssues
    }
  }, [allIssues, filter])

  const handleCreate = async () => {
    const title = newTitle.trim()
    if (!title) return
    await createMut.mutateAsync({
      negotiationId,
      title,
      category: newCategory.trim() || undefined,
      is_deal_breaker: newDealBreaker,
    })
    setNewTitle('')
    setNewCategory('')
    setNewDealBreaker(false)
  }

  if (isLoading || !issues) return null

  const filters: { key: Filter; label: string }[] = [
    { key: 'all', label: t('negotiate.issues.filter.all') },
    { key: 'open', label: t('negotiate.issues.filter.open') },
    { key: 'deal-breakers', label: t('negotiate.issues.filter.dealBreakers') },
    { key: 'resolved', label: t('negotiate.issues.filter.resolved') },
  ]

  const expanded = alwaysExpanded || isExpanded

  return (
    <div className={alwaysExpanded ? '' : 'border-b border-neutral-100 dark:border-neutral-800'}>
      {!alwaysExpanded && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-3 py-1.5 flex items-center gap-1.5 text-[10px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
        >
          <Scale size={10} />
          {t('negotiate.issues.title')}
          {openCount > 0 && (
            <span className="text-neutral-400 dark:text-neutral-500 normal-case tracking-normal">
              {openCount} {t('negotiate.issues.open')}
            </span>
          )}
          {dealBreakerCount > 0 && (
            <span className="px-1 py-0.5 text-[9px] font-bold rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 normal-case tracking-normal">
              {dealBreakerCount} {t('negotiate.issues.dealBreaker')}
            </span>
          )}
          <span className="flex-1" />
          {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
        </button>
      )}

      {expanded && (
        <div className="animate-fade-in">
          {/* Filter pills */}
          <div className="px-3 pb-1.5 flex items-center gap-1">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-2 py-0.5 text-[10px] font-medium rounded-full transition-colors ${
                  filter === f.key
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                    : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Issues list */}
          <div className="px-3 pb-2">
            {filtered.length === 0 ? (
              <p className="py-3 text-center text-[10px] text-neutral-400 dark:text-neutral-500">
                {allIssues.length === 0
                  ? t('negotiate.issues.empty')
                  : t('negotiate.issues.emptyFilter')}
              </p>
            ) : (
              <div className="space-y-0.5">
                {filtered.map((issue) => (
                  <IssueRow
                    key={issue.id}
                    issue={issue}
                    negotiationId={negotiationId}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Inline create */}
          {showCreate ? (
            <div className="px-3 pb-2 space-y-1.5">
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreate()
                    if (e.key === 'Escape') { setShowCreate(false); setNewTitle(''); setNewDealBreaker(false) }
                  }}
                  placeholder={t('negotiate.issues.titlePlaceholder')}
                  className="flex-1 px-2 py-1 text-[10px] rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  autoFocus
                />
                <button
                  onClick={handleCreate}
                  disabled={!newTitle.trim() || createMut.isPending}
                  className="px-2 py-1 text-[10px] font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 rounded transition-colors"
                >
                  {t('negotiate.issues.add')}
                </button>
                <button
                  onClick={() => { setShowCreate(false); setNewTitle(''); setNewDealBreaker(false) }}
                  className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                >
                  <X size={10} />
                </button>
              </div>
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder={t('negotiate.issues.categoryPlaceholder', 'Category (optional)')}
                className="w-full px-2 py-1 text-[10px] rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
              <label className="flex items-center gap-1.5 text-[10px] text-neutral-500 dark:text-neutral-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newDealBreaker}
                  onChange={(e) => setNewDealBreaker(e.target.checked)}
                  className="w-3 h-3 rounded border-neutral-300 text-red-600 focus:ring-red-400"
                />
                {t('negotiate.issues.dealBreaker')}
              </label>
            </div>
          ) : (
            <div className="px-3 pb-2">
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-1 text-[10px] font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                <Plus size={10} />
                {t('negotiate.issues.new')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Issue Row ────────────────────────────────────────────

function IssueRow({
  issue,
  negotiationId,
}: {
  issue: NegotiationIssue
  negotiationId: number
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(issue.title)
  const [editCategory, setEditCategory] = useState(issue.category || '')
  const updateMut = useUpdateIssue()
  const deleteMut = useDeleteIssue()

  const statusStyle = STATUS_STYLES[issue.status]
  const isResolved = issue.status === 'ACCEPTED'

  const handleStatusChange = (newStatus: IssueStatus) => {
    updateMut.mutate({ negotiationId, issueId: issue.id, status: newStatus })
  }

  const handleSaveEdit = useCallback(async () => {
    if (!editTitle.trim()) return
    await updateMut.mutateAsync({
      negotiationId,
      issueId: issue.id,
      title: editTitle.trim(),
      category: editCategory.trim() || undefined,
    })
    setIsEditing(false)
  }, [negotiationId, issue.id, editTitle, editCategory, updateMut])

  const handleStartEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setEditTitle(issue.title)
    setEditCategory(issue.category || '')
    setIsEditing(true)
    setExpanded(true)
  }, [issue])

  if (isEditing) {
    return (
      <div className="py-1.5 px-1 rounded bg-neutral-50 dark:bg-neutral-800/50 space-y-1">
        <input
          type="text"
          value={editTitle}
          onChange={e => setEditTitle(e.target.value)}
          placeholder={t('negotiate.issues.title', 'Issue title')}
          className="w-full text-[10px] px-1.5 py-0.5 rounded border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200"
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setIsEditing(false) }}
        />
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={editCategory}
            onChange={e => setEditCategory(e.target.value)}
            placeholder={t('negotiate.issues.category', 'Category')}
            className="flex-1 text-[9px] px-1.5 py-0.5 rounded border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
          />
          <button
            onClick={handleSaveEdit}
            disabled={updateMut.isPending || !editTitle.trim()}
            className="shrink-0 p-0.5 text-success-600 hover:text-success-700 disabled:opacity-50"
          >
            <Check size={12} />
          </button>
          <button
            onClick={() => setIsEditing(false)}
            className="shrink-0 p-0.5 text-neutral-400 hover:text-neutral-600"
          >
            <X size={12} />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="group py-1 rounded hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
      {/* Main row */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-1.5 text-left"
      >
        {issue.is_deal_breaker && !isResolved && (
          <AlertOctagon size={10} className="shrink-0 text-red-500" />
        )}
        {isResolved && (
          <span className="shrink-0 text-[10px]">✅</span>
        )}
        <span className={`flex-1 text-[10px] leading-tight truncate ${
          isResolved ? 'text-neutral-400 dark:text-neutral-500' : 'text-neutral-800 dark:text-neutral-200'
        }`}>
          {issue.title}
        </span>
        {issue.category && (
          <span className="shrink-0 px-1 py-0.5 text-[8px] font-medium rounded bg-neutral-100 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400">
            {issue.category}
          </span>
        )}
        <span className={`shrink-0 px-1.5 py-0.5 text-[8px] font-medium rounded ${statusStyle.bg} ${statusStyle.text}`}>
          {t(`negotiate.issues.status.${issue.status.toLowerCase()}`)}
        </span>
        {!isResolved && (
          <button
            onClick={handleStartEdit}
            className="shrink-0 p-0.5 opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-primary-500 transition-all"
            title={t('negotiate.editIssue', 'Edit')}
          >
            <Pencil size={10} />
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); deleteMut.mutate({ negotiationId, issueId: issue.id }) }}
          className="shrink-0 p-0.5 opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-500 transition-all"
        >
          <X size={10} />
        </button>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="mt-1 ml-4 space-y-1 animate-fade-in">
          {/* Positions (internal) */}
          {issue.our_position && (
            <div className="px-2 py-1 text-[9px] rounded bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200">
              <span className="font-medium">{t('negotiate.issues.ourPosition')}:</span> {issue.our_position}
            </div>
          )}
          {issue.their_position && (
            <div className="px-2 py-1 text-[9px] rounded bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200">
              <span className="font-medium">{t('negotiate.issues.theirPosition')}:</span> {issue.their_position}
            </div>
          )}
          {issue.agreed_resolution && (
            <div className="px-2 py-1 text-[9px] rounded bg-success-50 dark:bg-success-900/20 text-success-800 dark:text-success-200">
              <span className="font-medium">{t('negotiate.issues.resolution')}:</span> {issue.agreed_resolution}
            </div>
          )}

          {/* Status actions */}
          {!isResolved && (
            <div className="flex items-center gap-1 pt-0.5">
              {issue.status === 'OPEN' && (
                <StatusButton label={t('negotiate.issues.action.propose')} onClick={() => handleStatusChange('PROPOSED')} />
              )}
              {(issue.status === 'PROPOSED' || issue.status === 'COUNTER_PROPOSED') && (
                <>
                  <StatusButton label={t('negotiate.issues.action.accept')} onClick={() => handleStatusChange('ACCEPTED')} variant="success" />
                  <StatusButton label={t('negotiate.issues.action.reject')} onClick={() => handleStatusChange('REJECTED')} variant="danger" />
                  <StatusButton label={t('negotiate.issues.action.defer')} onClick={() => handleStatusChange('DEFERRED')} />
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function StatusButton({
  label,
  onClick,
  variant = 'neutral',
}: {
  label: string
  onClick: () => void
  variant?: 'neutral' | 'success' | 'danger'
}) {
  const colors = {
    neutral: 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700',
    success: 'text-success-700 dark:text-success-300 hover:bg-success-50 dark:hover:bg-success-900/30',
    danger: 'text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30',
  }
  return (
    <button
      onClick={onClick}
      className={`px-1.5 py-0.5 text-[9px] font-medium rounded transition-colors ${colors[variant]}`}
    >
      {label}
    </button>
  )
}
